<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('settings.settingsLabel') }}</span>
        <h3 class="page-title">{{ $t('settings.country.addNewCountry') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/ustawienia/kraje')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('settings.country.addCountry') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !allDataLoaded }">
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="country-new-form" tag="form">
              <d-row>
                <d-col cols="6">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.country.countryName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.country.countryInvoiceCode') }}:
                    </label>

                    <validation-provider
                      rules="required|only-big-letters|min:2|max:3"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.country.countryAddressCode') }}:
                    </label>

                    <validation-provider
                      rules="required|only-big-letters|min:2|max:3"
                      name="address_code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="address_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <div class="form-group pt-4">
                    <label>
                      <d-checkbox
                        inline
                        toggle-small
                        v-model="isEU"
                        toggle>
                        {{ $t('settings.country.isEU') }}
                      </d-checkbox>
                    </label>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group pt-4">
                    <label>
                      <d-checkbox
                        inline
                        toggle-small
                        v-model="verifyTaxNumber"
                        toggle>
                        {{ $t('settings.country.verifyTaxNumber') }}
                      </d-checkbox>
                    </label>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.language') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="language-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="defaultLanguageID"
                        :state="errors.length ? 'invalid' : null"
                        :options="languagesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="3">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('settings.currency.currency') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="currency-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="defaultCurrencyID"
                        :state="errors.length ? 'invalid' : null"
                        :options="currenciesOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
            </validation-observer>
          </d-tab>

          <d-tab :title="$t('settings.country.translations')">
            <p class="text-center">
              {{ $t('settings.country.addingTranslationsAvailableAfterSave') }}
            </p>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'country-new',
  computed: {
    allDataLoaded () {
      return this.loadedLanguages && this.loadedCurrencies;
    },
    languagesOptions () {
      return [{ value: '', text: this.$t('settings.language.chooseLanguage') }].concat(this.languages.map(language => ({
        value: language.id,
        text: language.name
      })));
    },
    currenciesOptions () {
      return [{ value: '', text: this.$t('shipments.popups.selectCurrency') }].concat(CommonGetters.getCurrenciesForSelect(this.currencies));
    }
  },
  data() {
    return {
      name: '',
      address_code: '',
      code: '',
      isEU: false,
      verifyTaxNumber: false,
      defaultLanguageID: '',
      defaultCurrencyID: '',
      currencies: [],
      languages: [],
      loadError: false,
      loadedLanguages: false,
      loadedCurrencies: false
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError',
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['country-new-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/countries/store',
        formData: {
          name: this.name,
          address_code: this.address_code,
          code: this.code,
          is_eu: this.isEU,
          verify_tax_number: +this.verifyTaxNumber,
          default_currency_id: this.defaultCurrencyID,
          default_language_id: this.defaultLanguageID
        },
        successTitle: this.$t('settings.country.addCountrySuccessTitle'),
        successText: this.$t('settings.country.addCountrySuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.country.addCountryErrorText'),
        successAction: () => {
          this.$router.push('/ustawienia/kraje')
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
